import { useCallback, useEffect, useRef, useState } from "react";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError, api } from "@/lib/apiClient";

type Json = Record<string, unknown>;

type ShadowStorageStatus = {
  auftraege_total?: number;
  auftraege_with_hero_project?: number;
  auftraege_with_hero_step?: number;
  auftraege_with_tracking_times?: number;
  auftraege_with_custom_fields?: number;
  hero_tracking_times_rows?: number;
  hero_partners_count?: number;
  hero_absences_count?: number;
  hero_contacts?: { count?: number; oldest_synced_at?: string | null; newest_synced_at?: string | null } | null;
  hero_absences?: { count?: number; oldest_synced_at?: string | null; newest_synced_at?: string | null } | null;
  hero_catalog_snapshots?: { key?: string; synced_at?: string | null }[];
  hero_customer_documents_count?: number;
  coverage_tracking_times_pct?: number | null;
  coverage_hero_step_pct?: number | null;
};

type StepState = "pending" | "running" | "ok" | "error";

type StepResult = {
  state: StepState;
  summary?: string;
  error?: string;
};

type StepDef = {
  id: string;
  label: string;
  hint?: string;
  run: (ctx: { vonJahr: number; bisJahr: number }) => Promise<string>;
};

function num(v: unknown): string {
  return typeof v === "number" ? String(v) : "?";
}

async function runSupplyProductsLoop(): Promise<string> {
  let offset = 0;
  let totalSynced = 0;
  let iterations = 0;
  // Der Endpunkt paginiert intern; Loop nur nötig, wenn er "partial" meldet.
  for (;;) {
    iterations += 1;
    if (iterations > 50) throw new Error("Abbruch: zu viele Teil-Läufe (>50)");
    const res = await api.post<Json>("/hero/sync-supply-products", { offset });
    totalSynced += typeof res.synced === "number" ? res.synced : 0;
    if (res.status === "partial" && typeof res.next_offset === "number") {
      offset = res.next_offset;
      continue;
    }
    return `${totalSynced} Produkte synchronisiert (${iterations} Lauf/Läufe)`;
  }
}

async function runDocumentDetailsLoop(): Promise<string> {
  let totalSynced = 0;
  let iterations = 0;
  for (;;) {
    iterations += 1;
    if (iterations > 200) throw new Error("Abbruch: zu viele Teil-Läufe (>200)");
    const res = await api.post<Json>("/hero/sync-document-details", {
      limit: 200,
      only_missing: true,
    });
    const selected = typeof res.selected === "number" ? res.selected : 0;
    totalSynced += typeof res.synced === "number" ? res.synced : 0;
    if (selected === 0) {
      return `${totalSynced} Dokument-Details synchronisiert (${iterations} Lauf/Läufe)`;
    }
  }
}

async function runPerYear(
  path: string,
  vonJahr: number,
  bisJahr: number,
  summaryKey: string,
): Promise<string> {
  const parts: string[] = [];
  for (let jahr = vonJahr; jahr <= bisJahr; jahr += 1) {
    const res = await api.post<Json>(path, { jahr });
    parts.push(`${jahr}: ${num(res[summaryKey])}`);
  }
  return parts.join(", ");
}

const STEPS: StepDef[] = [
  {
    id: "partners",
    label: "1. Partner (Mitarbeiter)",
    run: async () => {
      const res = await api.post<Json>("/hero/sync-partners");
      return `${num(res.synced)} Partner synchronisiert`;
    },
  },
  {
    id: "kunden-map",
    label: "2. Kunden-Map (inkl. gelöschter Kunden)",
    run: async () => {
      const res = await api.get<unknown>("/hero/kunden/all");
      return Array.isArray(res) ? `${res.length} Kunden geladen` : "geladen";
    },
  },
  {
    id: "contacts",
    label: "3. Kontakte-Snapshot (Adressen + Ansprechpartner)",
    hint: "Nur wenn HERO_KONTAKTE_SYNC=true. Standardmäßig deaktiviert.",
    run: async () => {
      const res = await api.post<Json>("/hero/sync-contacts");
      return `${num(res.synced)} Kontakte synchronisiert`;
    },
  },
  {
    id: "orte",
    label: "4. Orte",
    run: async () => {
      await api.get<Json>("/hero/sync-orte");
      return "Orte synchronisiert";
    },
  },
  {
    id: "ansprechpartner",
    label: "5. Ansprechpartner",
    hint: "Nur wenn HERO_KONTAKTE_SYNC=true. Standardmäßig deaktiviert.",
    run: async () => {
      await api.post<Json>("/hero/sync-hero-ansprechpartner");
      await api.post<Json>("/hero/sync-hero-alle-ansprechpartner");
      return "Ansprechpartner synchronisiert";
    },
  },
  {
    id: "catalogs",
    label: "6. Kataloge (Gewerke/Niederlassungen/Projekttypen/Texte/Custom-Field-Schemas)",
    run: async () => {
      const res = await api.post<Json>("/hero/sync-catalogs");
      const keys = res && typeof res === "object" ? Object.keys(res) : [];
      return `Kataloge synchronisiert (${keys.length} Einträge)`;
    },
  },
  {
    id: "supply-products",
    label: "7. Supply-Products (Artikel + Preise, paginiert)",
    hint: "Nur wenn HERO_ARTIKEL_SYNC=true. Standardmäßig deaktiviert.",
    run: () => runSupplyProductsLoop(),
  },
  {
    id: "project-meta",
    label: "8. Projekt-Meta (Status/Steps) je Jahr",
    run: ({ vonJahr, bisJahr }) =>
      runPerYear("/hero/sync-project-meta", vonJahr, bisJahr, "projects"),
  },
  {
    id: "tracking-times",
    label: "9. Tracking-Times je Jahr",
    run: ({ vonJahr, bisJahr }) =>
      runPerYear("/hero/sync-tracking-times", vonJahr, bisJahr, "projects_synced"),
  },
  {
    id: "absences",
    label: "10. Abwesenheiten (−12 bis +24 Monate)",
    run: async () => {
      const res = await api.post<Json>("/hero/sync-absences");
      return `${num(res.synced)} Abwesenheiten synchronisiert`;
    },
  },
  {
    id: "customer-documents",
    label: "11. Customer-Documents Catch-up (10 Jahre)",
    run: async () => {
      const res = await api.post<Json>("/hero/sync-customer-documents-last-days/3650");
      return `Dokumente: ${JSON.stringify(res).slice(0, 200)}`;
    },
  },
  {
    id: "document-details",
    label: "12. Document-Details (nur fehlende, paginiert)",
    hint: "Läuft automatisch weiter, bis keine fehlenden Dokumente mehr übrig sind.",
    run: () => runDocumentDetailsLoop(),
  },
  {
    id: "custom-field-records",
    label: "13. Custom-Field-Records",
    run: async () => {
      const res = await api.get<unknown>("/hero/custom-fields-records");
      return Array.isArray(res) ? `${res.length} Werte synchronisiert` : "synchronisiert";
    },
  },
  {
    id: "doc-status",
    label: "14. Hero-Dokumentstatus (Maintenance)",
    run: async () => {
      const res = await api.post<Json>("/maintenance/sync-hero-doc-status");
      return `Dokumentstatus: ${JSON.stringify(res).slice(0, 200)}`;
    },
  },
  {
    id: "projects",
    label: "15. Hero-Projekte (alle project_matches, für Auswertung)",
    hint: "Spiegelt alle Hero-Projekte in die lokale Tabelle hero_projekte (paginiert, läuft bis alle Seiten geladen sind).",
    run: async () => {
      let offset = 0;
      let total = 0;
      let pages = 0;
      // Fortsetzen, bis der Server complete=true meldet (Schutz: 50 Runden).
      for (let round = 0; round < 50; round += 1) {
        const res = await api.post<Json>("/hero/sync-projects", {
          first: 200,
          offset,
          max_pages: 20,
        });
        total += typeof res.synced === "number" ? res.synced : 0;
        pages += typeof res.pages === "number" ? res.pages : 0;
        offset = typeof res.next_offset === "number" ? res.next_offset : offset;
        if (res.complete) break;
      }
      return `${total} Projekte synchronisiert (${pages} Seiten)`;
    },
  },
];

export function HeroFinalSyncSection() {
  const currentYear = new Date().getFullYear();
  const [vonJahr, setVonJahr] = useState(currentYear - 3);
  const [bisJahr, setBisJahr] = useState(currentYear);
  const [results, setResults] = useState<Record<string, StepResult>>({});
  const [runningAll, setRunningAll] = useState(false);
  const [status, setStatus] = useState<ShadowStorageStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const anyRunning = runningAll || Object.values(results).some((r) => r.state === "running");
  const cancelRef = useRef(false);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    setStatusError(null);
    try {
      setStatus(await api.get<ShadowStorageStatus>("/hero/shadow-storage/status"));
    } catch (err) {
      setStatusError(
        err instanceof ApiError ? err.message : "Shadow-Status konnte nicht geladen werden.",
      );
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  async function runStep(step: StepDef): Promise<boolean> {
    setResults((prev) => ({ ...prev, [step.id]: { state: "running" } }));
    try {
      const summary = await step.run({ vonJahr, bisJahr });
      setResults((prev) => ({ ...prev, [step.id]: { state: "ok", summary } }));
      return true;
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Unbekannter Fehler";
      setResults((prev) => ({ ...prev, [step.id]: { state: "error", error: message } }));
      return false;
    }
  }

  async function runAll() {
    if (!window.confirm(
      "Alle Schritte der finalen Synchronisierung nacheinander ausführen? " +
      "Das kann je nach Datenmenge lange dauern.",
    )) {
      return;
    }
    cancelRef.current = false;
    setRunningAll(true);
    try {
      for (const step of STEPS) {
        if (cancelRef.current) break;
        const ok = await runStep(step);
        if (!ok) break; // Bei Fehler stoppen, damit die Reihenfolge gewahrt bleibt.
      }
    } finally {
      setRunningAll(false);
      void loadStatus();
    }
  }

  function catalogSummary(s: ShadowStorageStatus): string {
    const list = s.hero_catalog_snapshots ?? [];
    if (!list.length) return "keine";
    return list.map((c) => c.key).join(", ");
  }

  return (
    <Card className="space-y-4 p-4 sm:p-5">
      <div>
        <h2 className="text-base font-semibold text-label">Finale Synchronisierung (Hero-freier Betrieb)</h2>
        <p className="mt-1 text-sm text-label-secondary">
          Führt die Schritte in Reihenfolge aus, damit alle Hero-Daten lokal in der
          Shadow-Datenbank liegen. Nach erfolgreicher Runde: Kill-Switch oben aktivieren
          und alle Hero-Scheduled-Jobs (Cron/Automationen) deaktivieren.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm text-label-secondary">
          Jahre von
          <input
            type="number"
            className="ml-2 w-24 rounded border border-separator bg-transparent px-2 py-1 text-label"
            value={vonJahr}
            min={2015}
            max={bisJahr}
            disabled={anyRunning}
            onChange={(e) => setVonJahr(Number(e.target.value))}
          />
        </label>
        <label className="text-sm text-label-secondary">
          bis
          <input
            type="number"
            className="ml-2 w-24 rounded border border-separator bg-transparent px-2 py-1 text-label"
            value={bisJahr}
            min={vonJahr}
            max={currentYear + 1}
            disabled={anyRunning}
            onChange={(e) => setBisJahr(Number(e.target.value))}
          />
        </label>
        <Button type="button" variant="primary" disabled={anyRunning} onClick={() => void runAll()}>
          {runningAll ? "Läuft…" : "Alle Schritte nacheinander ausführen"}
        </Button>
        {runningAll ? (
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              cancelRef.current = true;
            }}
          >
            Nach aktuellem Schritt stoppen
          </Button>
        ) : null}
      </div>

      <ul className="divide-y divide-separator rounded border border-separator">
        {STEPS.map((step) => {
          const res = results[step.id];
          return (
            <li key={step.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-label">{step.label}</div>
                {step.hint ? (
                  <div className="text-xs text-label-secondary">{step.hint}</div>
                ) : null}
                {res?.state === "ok" && res.summary ? (
                  <div className="text-xs text-green-600 dark:text-green-400">{res.summary}</div>
                ) : null}
                {res?.state === "error" ? (
                  <div className="text-xs text-red-600 dark:text-red-400">Fehler: {res.error}</div>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                {res?.state === "running" ? <Spinner /> : null}
                <Button
                  type="button"
                  variant="secondary"
                  disabled={anyRunning}
                  onClick={() => {
                    void runStep(step).then(() => void loadStatus());
                  }}
                >
                  {res?.state === "ok" ? "Erneut" : "Start"}
                </Button>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-label">Shadow-Speicher-Status</h3>
          <Button
            type="button"
            variant="secondary"
            disabled={statusLoading}
            onClick={() => void loadStatus()}
          >
            Aktualisieren
          </Button>
          {statusLoading ? <Spinner /> : null}
        </div>
        {statusError ? <Alert variant="error">{statusError}</Alert> : null}
        {status ? (
          <div className="grid grid-cols-1 gap-x-6 gap-y-1 text-sm text-label-secondary sm:grid-cols-2">
            <div>Aufträge gesamt: {num(status.auftraege_total)}</div>
            <div>… mit Hero-Projekt: {num(status.auftraege_with_hero_project)}</div>
            <div>
              … mit Hero-Step: {num(status.auftraege_with_hero_step)}
              {status.coverage_hero_step_pct != null ? ` (${status.coverage_hero_step_pct}%)` : ""}
            </div>
            <div>
              … mit Tracking-Times: {num(status.auftraege_with_tracking_times)}
              {status.coverage_tracking_times_pct != null
                ? ` (${status.coverage_tracking_times_pct}%)`
                : ""}
            </div>
            <div>… mit Custom-Fields: {num(status.auftraege_with_custom_fields)}</div>
            <div>Tracking-Time-Zeilen: {num(status.hero_tracking_times_rows)}</div>
            <div>Partner: {num(status.hero_partners_count)}</div>
            <div>
              Abwesenheiten: {num(status.hero_absences_count)}
              {status.hero_absences?.newest_synced_at
                ? ` (Stand ${new Date(status.hero_absences.newest_synced_at).toLocaleString("de-DE")})`
                : ""}
            </div>
            <div>
              Kontakte-Snapshot: {num(status.hero_contacts?.count)}
              {status.hero_contacts?.newest_synced_at
                ? ` (Stand ${new Date(status.hero_contacts.newest_synced_at).toLocaleString("de-DE")})`
                : ""}
            </div>
            <div>Customer-Documents: {num(status.hero_customer_documents_count)}</div>
            <div className="sm:col-span-2">Katalog-Snapshots: {catalogSummary(status)}</div>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
